import React, { useEffect, useState, useCallback } from 'react';
import { getLeaveRequests, updateLeaveStatus, deleteLeaveRequest, LeaveRequestData } from '../../services/leaveService';
import { getSwaps, updateSwapStatus, deleteSwapRequest, SwapRequestData } from '../../services/swapService';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';

const statusStyles: Record<string, string> = {
    PENDING: 'bg-amber-50 text-amber-700 border border-amber-200',
    APPROVED: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    REJECTED: 'bg-red-50 text-red-700 border border-red-200',
};

const leaveTypeIcons: Record<string, string> = {
    ANNUAL: '🏖️', SICK: '🏥', PERSONAL: '👤', UNPAID: '💰',
};

const shiftTypeLabels: Record<string, string> = {
    CLEARER: 'Clearing',
    LHR: 'LHR',
    OFFICE: 'Office',
    WAREHOUSE: 'Warehouse',
    CONTROL: 'Control',
};

const impliedLocation: Record<string, string> = {
    LHR: 'Heathrow',
    OFFICE: 'Office',
    CONTROL: 'Control Room',
    WAREHOUSE: 'Warehouse',
};

const getShiftLocation = (type?: string, location?: string | null) => location || (type ? impliedLocation[type] : '') || '';
const shiftTypeLabel = (type?: string) => (type ? shiftTypeLabels[type] || type : 'Shift');
const formatDateTime = (value?: string) => value ? format(parseISO(value), 'EEE, d MMM yyyy · HH:mm') : 'Unknown date';
const formatTime = (value?: string) => value ? format(parseISO(value), 'HH:mm') : '--:--';
const formatPosted = (value?: string) => value ? format(parseISO(value), 'd MMM yyyy HH:mm') : 'Unknown';

const ShiftDetail = ({ title, shift, ownerLabel }: { title: string; shift?: any; ownerLabel?: string }) => {
    if (!shift) return null;
    const location = getShiftLocation(shift.type, shift.location);
    const owner = ownerLabel || shift.user?.name;
    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</p>
                <span className="rounded bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 border border-slate-200">
                    {shiftTypeLabel(shift.type)}
                </span>
            </div>
            <p className="font-semibold text-slate-800">{formatDateTime(shift.startTime)}</p>
            <p className="text-sm text-slate-500">
                {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                {location ? ` · ${location}` : ''}
            </p>
            {owner && <p className="text-xs text-slate-400 mt-1">Assigned to {owner}</p>}
        </div>
    );
};

const ReviewDetail = ({ item }: { item: { reviewedBy?: { name: string } | null; reviewedAt?: string; reviewedByRole?: string | null } }) => {
    if (!item.reviewedAt && !item.reviewedBy && !item.reviewedByRole) return null;
    return (
        <p className="text-xs text-slate-500">
            Reviewed by {item.reviewedBy?.name || 'Unknown'}{item.reviewedByRole ? ` (${item.reviewedByRole})` : ''}{item.reviewedAt ? ` on ${formatPosted(item.reviewedAt)}` : ''}
        </p>
    );
};

type StatusFilter = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';

const AdminDashboard: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'leaves' | 'swaps'>('leaves');
    const [leaves, setLeaves] = useState<LeaveRequestData[]>([]);
    const [swaps, setSwaps] = useState<SwapRequestData[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');
    const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Action modal state
    const [actionModal, setActionModal] = useState<{
        type: 'approve' | 'reject' | 'delete';
        target: 'leave' | 'swap';
        id: string;
        name: string;
    } | null>(null);
    const [adminNotes, setAdminNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const showToast = (type: 'success' | 'error', message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 4000);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [leaveData, swapData] = await Promise.all([
                getLeaveRequests('all'),
                getSwaps('all'),
            ]);
            setLeaves(Array.isArray(leaveData) ? leaveData : leaveData?.data || []);
            setSwaps(Array.isArray(swapData) ? swapData : swapData?.data || []);
        } catch (err) {
            console.error('Failed to load approvals', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAction = async () => {
        if (!actionModal) return;
        setActionLoading(true);
        try {
            if (actionModal.type === 'delete') {
                if (actionModal.target === 'leave') await deleteLeaveRequest(actionModal.id);
                else await deleteSwapRequest(actionModal.id);
                showToast('success', 'Request deleted');
            } else {
                const status = actionModal.type === 'approve' ? 'APPROVED' : 'REJECTED';
                let result: any;
                if (actionModal.target === 'leave') {
                    result = await updateLeaveStatus(actionModal.id, status, adminNotes || undefined);
                } else {
                    result = await updateSwapStatus(actionModal.id, status, adminNotes || undefined);
                }
                const affectedCount = result?.affectedShiftCount;
                showToast(
                    'success',
                    `Request ${actionModal.type === 'approve' ? 'approved' : 'rejected'}${affectedCount ? ` · ${affectedCount} shift${affectedCount === 1 ? '' : 's'} updated` : ''}`
                );
            }
            fetchData();
        } catch (err: any) {
            showToast('error', err.response?.data?.message || 'Action failed');
        } finally {
            setActionLoading(false);
            setActionModal(null);
            setAdminNotes('');
        }
    };

    const filteredLeaves = statusFilter === 'ALL' ? leaves : leaves.filter(l => l.status === statusFilter);
    const filteredSwaps = statusFilter === 'ALL' ? swaps : swaps.filter(s => s.status === statusFilter);

    const pendingLeavesCount = leaves.filter(l => l.status === 'PENDING').length;
    const pendingSwapsCount = swaps.filter(s => s.status === 'PENDING').length;
    const actionSwap = actionModal?.target === 'swap' ? swaps.find(s => s.id === actionModal.id) : undefined;
    const actionLeave = actionModal?.target === 'leave' ? leaves.find(l => l.id === actionModal.id) : undefined;
    const actionDescription = (() => {
        if (!actionModal || actionModal.type === 'delete') return '';
        if (actionSwap) {
            const date = format(parseISO(actionSwap.shift.startTime), 'EEE, d MMM yyyy');
            if (actionModal.type === 'approve') {
                if (actionSwap.swapType === 'SWAP') {
                    return `Approve swap: ${actionSwap.requester.name} and ${actionSwap.targetUser?.name || 'the acceptor'} will exchange these two shifts. Original shift: ${date}.`;
                }
                return `Approve cover: ${actionSwap.targetUser?.name || 'the acceptor'} will cover ${actionSwap.requester.name}'s shift on ${date}.`;
            }
            return `Reject ${actionSwap.swapType === 'SWAP' ? 'swap' : 'cover'} request for ${actionSwap.requester.name}'s shift on ${date}.`;
        }
        if (actionLeave) {
            return `${actionModal.type === 'approve' ? 'Approve' : 'Reject'} ${actionLeave.user?.name || 'this user'}'s ${actionLeave.type.toLowerCase()} leave from ${format(parseISO(actionLeave.startDate), 'd MMM')} to ${format(parseISO(actionLeave.endDate), 'd MMM yyyy')}.`;
        }
        return '';
    })();

    const StatusFilterPills = () => (
        <div className="flex gap-1 flex-wrap">
            {(['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as StatusFilter[]).map(f => (
                <button
                    key={f}
                    onClick={() => setStatusFilter(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        statusFilter === f
                            ? f === 'PENDING' ? 'bg-amber-100 text-amber-700'
                                : f === 'APPROVED' ? 'bg-emerald-100 text-emerald-700'
                                    : f === 'REJECTED' ? 'bg-red-100 text-red-700'
                                        : 'bg-slate-200 text-slate-700'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                >
                    {f === 'ALL' ? 'All' : f.charAt(0) + f.slice(1).toLowerCase()}
                    {f === 'PENDING' && (activeTab === 'leaves' ? pendingLeavesCount : pendingSwapsCount) > 0 && (
                        <span className="ml-1.5 px-1.5 py-0.5 bg-white/60 rounded-full text-[10px]">
                            {activeTab === 'leaves' ? pendingLeavesCount : pendingSwapsCount}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );

    const ActionButtons = ({ id, target, name, status, canApprove = true }: {
        id: string; target: 'leave' | 'swap'; name: string; status: string; canApprove?: boolean;
    }) => (
        <div className="flex gap-2">
            {status === 'PENDING' && canApprove && (
                <>
                    <button
                        onClick={() => setActionModal({ type: 'approve', target, id, name })}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                        ✓ Approve
                    </button>
                    <button
                        onClick={() => setActionModal({ type: 'reject', target, id, name })}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium rounded-lg border border-red-200 transition-colors"
                    >
                        ✕ Reject
                    </button>
                </>
            )}
            <button
                onClick={() => setActionModal({ type: 'delete', target, id, name })}
                className="px-2 py-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
            </button>
        </div>
    );

    return (
        <div className="max-w-5xl mx-auto">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 text-sm font-medium ${
                    toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                }`} style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    {toast.type === 'success' ? '✓' : '✕'} {toast.message}
                </div>
            )}

            {/* Action Modal */}
            {actionModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" style={{ animation: 'pageFadeIn 0.15s ease-out' }}>
                        <h3 className="text-lg font-bold text-slate-800 mb-1">
                            {actionModal.type === 'approve' ? '✓ Approve Request' :
                                actionModal.type === 'reject' ? '✕ Reject Request' : '🗑️ Delete Request'}
                        </h3>
                        <p className="text-sm text-slate-500 mb-4">
                            {actionModal.type === 'delete'
                                ? `Permanently delete ${actionModal.name}'s request? This cannot be undone.`
                                : actionDescription || `${actionModal.type === 'approve' ? 'Approve' : 'Reject'} ${actionModal.name}'s request?`
                            }
                        </p>

                        {actionModal.type !== 'delete' && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin Notes <span className="text-slate-400 font-normal">(optional)</span></label>
                                <textarea
                                    value={adminNotes}
                                    onChange={e => setAdminNotes(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm h-20 resize-none"
                                    placeholder="Add notes visible to the employee..."
                                />
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => { setActionModal(null); setAdminNotes(''); }}
                                disabled={actionLoading}
                                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAction}
                                disabled={actionLoading}
                                className={`flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                    actionModal.type === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700'
                                        : actionModal.type === 'reject' ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-slate-700 hover:bg-slate-800'
                                }`}
                            >
                                {actionLoading && <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />}
                                {actionModal.type === 'approve' ? 'Approve' : actionModal.type === 'reject' ? 'Reject' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Request Approvals</h1>
                <p className="text-slate-500 text-sm mt-1">Review and manage leave requests and shift swap approvals</p>
            </div>

            {/* Tabs + Filter */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
                    <button
                        onClick={() => setActiveTab('leaves')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                            activeTab === 'leaves' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                        }`}
                    >
                        🏖️ Leave Requests
                        {pendingLeavesCount > 0 && (
                            <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">{pendingLeavesCount}</span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('swaps')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                            activeTab === 'swaps' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                        }`}
                    >
                        🔄 Shift Swaps
                        {pendingSwapsCount > 0 && (
                            <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">{pendingSwapsCount}</span>
                        )}
                    </button>
                </div>
                <StatusFilterPills />
            </div>

            {/* Leave Requests Tab */}
            {activeTab === 'leaves' && (
                <div style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    {loading ? (
                        <div className="space-y-3">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                                    <div className="flex gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-slate-200" />
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-slate-200 rounded w-1/3" />
                                            <div className="h-3 bg-slate-100 rounded w-1/2" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filteredLeaves.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                            <div className="text-4xl mb-3 text-slate-300">—</div>
                            <h3 className="text-lg font-semibold text-slate-700">
                                {statusFilter === 'PENDING' ? 'No pending requests' : 'No requests found'}
                            </h3>
                            <p className="text-slate-500 text-sm mt-1">
                                {statusFilter === 'PENDING' ? 'All caught up! Check back later.' : 'Try changing the filter.'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredLeaves.map(request => {
                                const days = differenceInCalendarDays(parseISO(request.endDate), parseISO(request.startDate)) + 1;
                                return (
                                    <div key={request.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 transition-all">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-lg shrink-0">
                                                {leaveTypeIcons[request.type] || '📅'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                                    <div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-semibold text-slate-800">{request.user?.name || 'Unknown'}</span>
                                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusStyles[request.status]}`}>
                                                                {request.status}
                                                            </span>
                                                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                                                                {request.affectedShiftCount ?? 0} affected shift{request.affectedShiftCount === 1 ? '' : 's'}
                                                            </span>
                                                        </div>
                                                        <p className="text-sm text-slate-500 mt-1">
                                                            <span className="font-medium text-slate-600">{request.type}</span>
                                                            <span className="mx-1.5">·</span>
                                                            {format(parseISO(request.startDate), 'MMM d')} — {format(parseISO(request.endDate), 'MMM d, yyyy')}
                                                            <span className="mx-1.5">·</span>
                                                            <span className="font-medium">{days} day{days !== 1 ? 's' : ''}</span>
                                                        </p>
                                                        <ReviewDetail item={request} />
                                                    </div>
                                                </div>
                                                {request.reason && (
                                                    <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                                                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Reason</p>
                                                        <p className="text-sm text-slate-600">{request.reason}</p>
                                                    </div>
                                                )}
                                                {request.adminNotes && (
                                                    <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
                                                        <span className="font-medium">Admin:</span> {request.adminNotes}
                                                    </div>
                                                )}
                                            </div>
                                            <ActionButtons
                                                id={request.id}
                                                target="leave"
                                                name={request.user?.name || 'user'}
                                                status={request.status}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Shift Swaps Tab */}
            {activeTab === 'swaps' && (
                <div style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    {loading ? (
                        <div className="space-y-3">
                            {[1, 2].map(i => (
                                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                                    <div className="flex gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-slate-200" />
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-slate-200 rounded w-1/2" />
                                            <div className="h-3 bg-slate-100 rounded w-1/3" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filteredSwaps.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                            <div className="text-4xl mb-3 text-slate-300">—</div>
                            <h3 className="text-lg font-semibold text-slate-700">
                                {statusFilter === 'PENDING' ? 'No pending swaps' : 'No swaps found'}
                            </h3>
                            <p className="text-slate-500 text-sm mt-1">
                                {statusFilter === 'PENDING' ? 'All caught up!' : 'Try changing the filter.'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredSwaps.map(swap => {
                                const isSwap = swap.swapType === 'SWAP';
                                return (
                                <div key={swap.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 transition-all">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-lg shrink-0">
                                            {isSwap ? '🔄' : '🤝'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${isSwap ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'bg-teal-50 text-teal-700 border border-teal-100'}`}>
                                                            {isSwap ? 'Swap' : 'Cover'}
                                                        </span>
                                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusStyles[swap.status]}`}>
                                                            {swap.status}
                                                        </span>
                                                    </div>
                                                    <p className="font-semibold text-slate-800 mt-2">
                                                        {swap.requester.name}
                                                        <span className="text-slate-400 font-normal mx-1">→</span>
                                                        {swap.targetUser ? swap.targetUser.name : <span className="text-blue-500 italic">Waiting for acceptor</span>}
                                                    </p>
                                                    <p className="text-xs text-slate-500 mt-1">Posted {formatPosted(swap.createdAt)}</p>
                                                    <ReviewDetail item={swap} />
                                                </div>
                                            </div>
                                            <div className={`grid gap-3 mt-3 ${isSwap ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
                                                <ShiftDetail title="Original shift" shift={swap.shift} ownerLabel={swap.requester.name} />
                                                {isSwap && <ShiftDetail title="Target shift" shift={swap.targetShift} ownerLabel={swap.targetShift?.user?.name || swap.targetUser?.name} />}
                                            </div>
                                            {!swap.targetUser && swap.status === 'PENDING' && (
                                                <p className="text-xs text-amber-600 mt-3 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                                                    Waiting for a qualified staff member to accept before approval is available.
                                                </p>
                                            )}
                                            {swap.adminNotes && (
                                                <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
                                                    <span className="font-medium">Admin:</span> {swap.adminNotes}
                                                </div>
                                            )}
                                        </div>
                                        <ActionButtons
                                            id={swap.id}
                                            target="swap"
                                            name={swap.requester.name}
                                            status={swap.status}
                                            canApprove={!!swap.targetUser}
                                        />
                                    </div>
                                </div>
                            );})}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
